import { type IntegrationConnection } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Field, Input } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  connectHousecall,
  disconnectHousecall,
  housecallConnectionQuery,
} from '../../shared/api/parties-queries.js';

/**
 * This business's Housecall Pro connection.
 *
 * The key is typed here once and never shown again, by anyone: it goes to an
 * encrypted vault that only the server can read back. What stays visible is
 * whether it works, when it last synced, and its last four characters, so a
 * person can tell which key is in use without the key being on screen.
 */
export function HousecallConnection({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const connection = useQuery(housecallConnectionQuery(organizationId));
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['integrations', organizationId] });

  const connect = useMutation({
    mutationFn: () => connectHousecall(organizationId, apiKey),
    onSuccess: () => {
      setApiKey('');
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The key was not saved.'),
  });

  const disconnect = useMutation({
    mutationFn: () => disconnectHousecall(organizationId),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The connection was not removed.'),
  });

  const current = connection.data ?? null;
  const live = current !== null && current.status !== 'disconnected';

  return (
    <Card>
      <CardHeader title="Housecall Pro" description={describe(current)} />
      <CardBody className="flex flex-col gap-3">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
        {current?.status === 'failing' && current.lastError !== null ? (
          <Alert tone="warning" title="The last sync failed">
            {current.lastError}
          </Alert>
        ) : null}

        {!canManage ? (
          <p className="text-sm text-content-muted">
            An owner or admin connects Housecall Pro for this business.
          </p>
        ) : (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              connect.mutate();
            }}
          >
            <div className="min-w-64 flex-1">
              <Field label={live ? 'Replace the API key' : 'API key'}>
                {({ inputProps }) => (
                  <Input
                    {...inputProps}
                    type="password"
                    autoComplete="off"
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder="From Housecall Pro, under App Store, API"
                  />
                )}
              </Field>
            </div>
            <Button
              type="submit"
              isLoading={connect.isPending}
              disabled={apiKey.trim().length < 10}
            >
              {live ? 'Replace key' : 'Connect'}
            </Button>
            {live ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => disconnect.mutate()}
                isLoading={disconnect.isPending}
              >
                Disconnect
              </Button>
            ) : null}
          </form>
        )}
      </CardBody>
    </Card>
  );
}

function describe(connection: IntegrationConnection | null): string {
  if (connection === null || connection.status === 'disconnected') {
    return 'Not connected. Connecting brings in customers and jobs, then keeps them current every night.';
  }
  const key = connection.secretHint === null ? '' : ` Key ending ${connection.secretHint}.`;
  const synced =
    connection.lastSyncedAt === null
      ? ' Not synced yet.'
      : ` Last synced ${new Date(connection.lastSyncedAt).toLocaleString('en-CA')}.`;
  return `${connection.status === 'failing' ? 'Connected, but failing.' : 'Connected.'}${key}${synced}`;
}
